// Body-part layer geometry (SA B4). DOM-free; shared by the live WebGL
// compositor (analytic capsule/ellipse SDFs in the shader), the still renderer
// (Canvas2D paths, also on the Node render worker) and the interaction solver.
//
// Every person is split into up to four premultiplied layers:
//   arms_back  = alpha ∩ capsules of the arms that go BEHIND a partner
//   body       = alpha − arms_back            (front arms stay in body as well and
//                                             are simply drawn again on top)
//   head       = alpha ∩ head ellipse
//   arms_front = alpha ∩ capsules of the arms that go IN FRONT of a partner
// Capsule = shoulder→elbow→wrist with a radius derived from the shoulder width,
// plus a hand disc. No web model separates left/right arms, and capsules from
// pose are practically free and good enough for arm-over-shoulder and hugs.
//
// All geometry is in SOURCE pixels (un-mirrored); the placement transform maps it
// onto the stage exactly like the image itself.

import { anchorFromFace, toStage, type EyeAnchor, type FaceBox, type Placement } from './layout';
import { ARM_JOINTS, KP, KP_COUNT, KP_STRIDE, kpV, kpX, kpY, kpZ, type PoseFrame, type Side } from './pose';

export interface Capsule { ax: number; ay: number; bx: number; by: number; r: number }
export interface Disc { x: number; y: number; r: number }
export interface Ellipse { cx: number; cy: number; rx: number; ry: number; rot: number }
export interface ArmGeom { side: Side; upper: Capsule; fore: Capsule | null; hand: Disc | null }

/** radii as a fraction of the shoulder width */
export const ARM_R = { upper: 0.17, fore: 0.145, hand: 0.25, startAlong: 0.22 };
/** minimum landmark visibility to trust a joint */
export const VIS_MIN = 0.35;

/** Eye anchor of a pose frame: Face Landmarker eyes → Pose eyes → legacy face box; plus the shoulder x. */
export function anchorOf(p: PoseFrame | null | undefined, fallback: FaceBox): EyeAnchor {
  const a = eyeAnchorOf(p, fallback);
  if (p?.kp && kpV(p, KP.leftShoulder) >= 0.6 && kpV(p, KP.rightShoulder) >= 0.6) {
    a.bx = (kpX(p, KP.leftShoulder) + kpX(p, KP.rightShoulder)) / 2;
  }
  return a;
}

function eyeAnchorOf(p: PoseFrame | null | undefined, fallback: FaceBox): EyeAnchor {
  if (p?.face) {
    const { eyeL, eyeR, ipd, roll } = p.face;
    return { ex: (eyeL[0] + eyeR[0]) / 2, ey: (eyeL[1] + eyeR[1]) / 2, ipd, roll };
  }
  if (p?.kp && kpV(p, KP.leftEye) > 0.6 && kpV(p, KP.rightEye) > 0.6) {
    const ax = kpX(p, KP.leftEye), ay = kpY(p, KP.leftEye), bx = kpX(p, KP.rightEye), by = kpY(p, KP.rightEye);
    const [l, r] = ax <= bx ? [[ax, ay], [bx, by]] : [[bx, by], [ax, ay]];
    const dx = (r[0] - l[0]) * p.aspect, dy = r[1] - l[1];
    // Pose "eye" landmarks sit a little wider than the pupils
    return { ex: (l[0] + r[0]) / 2, ey: (l[1] + r[1]) / 2, ipd: Math.hypot(dx, dy) * 0.95, roll: Math.atan2(dy, dx) };
  }
  return anchorFromFace(fallback);
}

const vis = (p: PoseFrame, i: number) => kpV(p, i) >= VIS_MIN;
const px = (p: PoseFrame, i: number, w: number, h: number): [number, number] => [kpX(p, i) * w, kpY(p, i) * h];

/** Shoulder width in source px, or null when shoulders are not visible. */
export function shoulderWidth(p: PoseFrame | null | undefined, w: number, h: number): number | null {
  if (!p?.kp || !vis(p, KP.leftShoulder) || !vis(p, KP.rightShoulder)) return null;
  const a = px(p, KP.leftShoulder, w, h), b = px(p, KP.rightShoulder, w, h);
  const d = Math.hypot(a[0] - b[0], a[1] - b[1]);
  return d > 4 ? d : null;
}

/** Hand centre in source px: mean of wrist / index / pinky (wrist alone when fingers are not visible). */
export function handPoint(p: PoseFrame, side: Side, w: number, h: number, hiddenOk = false): [number, number] | null {
  const j = ARM_JOINTS[side];
  if (!vis(p, j.wrist) && !hiddenOk) return null;
  const pts = [j.wrist, j.index, j.pinky].filter((i) => i === j.wrist || vis(p, i)).map((i) => px(p, i, w, h));
  return [pts.reduce((a, q) => a + q[0], 0) / pts.length, pts.reduce((a, q) => a + q[1], 0) / pts.length];
}

/**
 * Arm capsules of one side, or null when shoulder/elbow are not visible.
 * `hiddenOk`: use the estimated wrist even when it is occluded (an arm behind a partner's back).
 */
export function armGeometry(p: PoseFrame | null | undefined, side: Side, w: number, h: number, hiddenOk = false): ArmGeom | null {
  const sw = shoulderWidth(p, w, h);
  if (!p || sw == null) return null;
  const j = ARM_JOINTS[side];
  if (!vis(p, j.elbow)) return null;
  const s = px(p, j.shoulder, w, h), e = px(p, j.elbow, w, h);
  // the first part of the upper arm belongs to the torso (avoids lifting the chest over a partner)
  const k = ARM_R.startAlong;
  const upper: Capsule = { ax: s[0] + (e[0] - s[0]) * k, ay: s[1] + (e[1] - s[1]) * k, bx: e[0], by: e[1], r: ARM_R.upper * sw };
  if (!vis(p, j.wrist) && !hiddenOk) return { side, upper, fore: null, hand: null };
  const wr = px(p, j.wrist, w, h);
  const fore: Capsule = { ax: e[0], ay: e[1], bx: wr[0], by: wr[1], r: ARM_R.fore * sw };
  const hp = handPoint(p, side, w, h, true)!;
  // hand centre a bit beyond the knuckle average, along the forearm direction
  const fx = wr[0] - e[0], fy = wr[1] - e[1], fl = Math.hypot(fx, fy) || 1;
  const hand: Disc = { x: hp[0] + (fx / fl) * 0.06 * sw, y: hp[1] + (fy / fl) * 0.06 * sw, r: ARM_R.hand * sw };
  return { side, upper, fore, hand };
}

/** Head + hair ellipse in source px from the eye anchor (IPD-relative proportions, rotated with the roll). */
export function headEllipse(a: EyeAnchor, w: number, h: number): Ellipse {
  const ipd = a.ipd * h;
  const up = 0.45 * ipd; // the head centre sits above the eye line
  return {
    cx: a.ex * w + Math.sin(a.roll) * up,
    cy: a.ey * h - Math.cos(a.roll) * up,
    rx: 1.75 * ipd,
    ry: 2.35 * ipd,
    rot: a.roll,
  };
}

/**
 * Skin region of the face (cheeks/forehead, no hair) in source px — where the
 * colour stats are measured, so matching follows the LIGHT, not the clothes.
 */
export function inFaceSkin(a: EyeAnchor, w: number, h: number, x: number, y: number) {
  const ipd = a.ipd * h;
  const dx = (x - a.ex * w) / (1.1 * ipd), dy = (y - (a.ey * h + 0.55 * ipd)) / (1.35 * ipd);
  return dx * dx + dy * dy < 1;
}

// ------------------------------------------------------------------ signed distances (used by tests and the CPU paths)

export function capsuleDist(c: Capsule, x: number, y: number) {
  const vx = c.bx - c.ax, vy = c.by - c.ay;
  const t = Math.max(0, Math.min(1, ((x - c.ax) * vx + (y - c.ay) * vy) / (vx * vx + vy * vy || 1)));
  return Math.hypot(x - (c.ax + vx * t), y - (c.ay + vy * t)) - c.r;
}

export function insideArm(g: ArmGeom, x: number, y: number) {
  return capsuleDist(g.upper, x, y) < 0 || (!!g.fore && capsuleDist(g.fore, x, y) < 0) ||
    (!!g.hand && Math.hypot(x - g.hand.x, y - g.hand.y) < g.hand.r);
}

// ------------------------------------------------------------------ solver input

/** What the interaction solver needs about one person, in UN-MIRRORED stage px. */
export interface SolverPerson {
  id: string;
  /** KP_COUNT × (x, y in stage px; z in shoulder widths; visibility), or null */
  kp: Float32Array | null;
  /** shoulder width in stage px */
  sw: number | null;
  /** head circle in stage px */
  head: { x: number; y: number; r: number } | null;
  /** source-relative IPD (fraction of source height), for the virtual depth */
  ipd: number | null;
  /** display-space slot x and user "front" override from the LayoutState */
  front: number;
}

/** 1/1024-of-stage-height grid (SA B5.6: deterministic comparisons on every device). */
export const gridQ = (v: number, stageH: number) => Math.round((v / stageH) * 1024) * stageH / 1024;

export function solverPerson(
  id: string, p: PoseFrame | null | undefined, anchor: EyeAnchor, pl: Placement,
  srcW: number, srcH: number, stageH: number, front = 0,
): SolverPerson {
  const swSrc = shoulderWidth(p, srcW, srcH);
  let kp: Float32Array | null = null;
  if (p?.kp && swSrc) {
    kp = new Float32Array(KP_COUNT * KP_STRIDE);
    for (let i = 0; i < KP_COUNT; i++) {
      const [x, y] = toStage(pl, kpX(p, i) * srcW, kpY(p, i) * srcH);
      kp[i * 4] = gridQ(x, stageH);
      kp[i * 4 + 1] = gridQ(y, stageH);
      kp[i * 4 + 2] = Math.round(((kpZ(p, i) * srcW) / swSrc) * 1024) / 1024;
      kp[i * 4 + 3] = kpV(p, i);
    }
  }
  const he = headEllipse(anchor, srcW, srcH);
  const [hx, hy] = toStage(pl, he.cx, he.cy);
  return {
    id, kp, front,
    sw: swSrc ? gridQ(swSrc * pl.s, stageH) : null,
    head: { x: gridQ(hx, stageH), y: gridQ(hy, stageH), r: gridQ(he.rx * pl.s, stageH) },
    ipd: p && (p.face || (p.kp && kpV(p, KP.leftEye) > 0.6 && kpV(p, KP.rightEye) > 0.6)) ? anchor.ipd : null,
  };
}
